import type { ActionHandler, EffectRecord } from '../types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Row = Record<string, any>

async function fetchRow(ctx: { supabase: any }, table: string, id: string): Promise<Row> {
  const { data } = await ctx.supabase.from(table).select('*').eq('id', id).single() as { data: Row | null }
  if (!data) throw new Error(`${table} row not found: ${id}`)
  return data
}

// ── ACQUIRE ──────────────────────────────────────────────────────────────────

export const handleAcquireEquipment: ActionHandler<'ACQUIRE_EQUIPMENT'> = async (ctx, payload) => {
  // If placing in a container, inherit its location
  let location = payload.location ?? 'backpack'
  let isCarried = location !== 'cached' && location !== 'ground'

  if (payload.parent_item_id) {
    const parent = await fetchRow(ctx, 'character_equipment', payload.parent_item_id)
    location = parent.location
    isCarried = parent.is_carried
  }

  const row = {
    character_id: ctx.characterId,
    item_name: payload.item_name,
    quantity: payload.quantity ?? 1,
    weight_lbs: payload.weight_lbs ?? 0,
    value_denarii: payload.value_denarii ?? null,
    location,
    is_carried: isCarried,
    parent_item_id: payload.parent_item_id ?? null,
  }

  const { data, error } = await ctx.supabase
    .from('character_equipment')
    .insert(row)
    .select()
    .single()

  if (error) throw error

  return {
    effects: [{
      character_id: ctx.characterId,
      target_table: 'character_equipment',
      target_row_id: data.id,
      target_field: '*',
      operation: 'INSERT' as const,
      old_value: null,
      new_value: data,
    }],
    label: `Acquire: ${payload.item_name}${(payload.quantity ?? 1) > 1 ? ` x${payload.quantity}` : ''}`,
  }
}

// ── DROP (with cascade) ──────────────────────────────────────────────────────

export const handleDropEquipment: ActionHandler<'DROP_EQUIPMENT'> = async (ctx, payload) => {
  const effects: EffectRecord[] = []

  // Fetch children first (for cascade delete + undo)
  const { data: children } = await ctx.supabase
    .from('character_equipment')
    .select('*')
    .eq('parent_item_id', payload.equipment_id) as { data: Row[] | null }

  // Delete children first (effects ordered so undo re-inserts parent before children)
  for (const child of (children ?? [])) {
    await ctx.supabase.from('character_equipment').delete().eq('id', child.id)
    effects.push({
      character_id: ctx.characterId,
      target_table: 'character_equipment',
      target_row_id: child.id,
      target_field: '*',
      operation: 'DELETE',
      old_value: child,
      new_value: null,
    })
  }

  // Delete parent
  const parent = await fetchRow(ctx, 'character_equipment', payload.equipment_id)
  await ctx.supabase.from('character_equipment').delete().eq('id', payload.equipment_id)
  effects.push({
    character_id: ctx.characterId,
    target_table: 'character_equipment',
    target_row_id: payload.equipment_id,
    target_field: '*',
    operation: 'DELETE',
    old_value: parent,
    new_value: null,
  })

  const childCount = (children ?? []).length
  return {
    effects,
    label: `Drop: ${parent.item_name}${childCount > 0 ? ` (+${childCount} contents)` : ''}`,
  }
}

// ── MOVE (with cascade) ──────────────────────────────────────────────────────

export const handleMoveEquipment: ActionHandler<'MOVE_EQUIPMENT'> = async (ctx, payload) => {
  const old = await fetchRow(ctx, 'character_equipment', payload.equipment_id)
  const isCarried = payload.location !== 'cached' && payload.location !== 'ground'
  const effects: EffectRecord[] = []

  // Update parent
  await ctx.supabase
    .from('character_equipment')
    .update({ location: payload.location, is_carried: isCarried })
    .eq('id', payload.equipment_id)

  if (old.location !== payload.location) {
    effects.push({ character_id: ctx.characterId, target_table: 'character_equipment', target_row_id: payload.equipment_id, target_field: 'location', operation: 'UPDATE', old_value: old.location, new_value: payload.location })
  }
  if (old.is_carried !== isCarried) {
    effects.push({ character_id: ctx.characterId, target_table: 'character_equipment', target_row_id: payload.equipment_id, target_field: 'is_carried', operation: 'UPDATE', old_value: old.is_carried, new_value: isCarried })
  }

  // Cascade to children
  const { data: children } = await ctx.supabase
    .from('character_equipment')
    .select('*')
    .eq('parent_item_id', payload.equipment_id) as { data: Row[] | null }

  for (const child of (children ?? [])) {
    if (child.location !== payload.location || child.is_carried !== isCarried) {
      await ctx.supabase
        .from('character_equipment')
        .update({ location: payload.location, is_carried: isCarried })
        .eq('id', child.id)

      if (child.location !== payload.location) {
        effects.push({ character_id: ctx.characterId, target_table: 'character_equipment', target_row_id: child.id, target_field: 'location', operation: 'UPDATE', old_value: child.location, new_value: payload.location })
      }
      if (child.is_carried !== isCarried) {
        effects.push({ character_id: ctx.characterId, target_table: 'character_equipment', target_row_id: child.id, target_field: 'is_carried', operation: 'UPDATE', old_value: child.is_carried, new_value: isCarried })
      }
    }
  }

  return { effects, label: `Move ${old.item_name} → ${payload.location}` }
}

// ── USE ITEM (decrement quantity) ────────────────────────────────────────────

export const handleUseItem: ActionHandler<'USE_ITEM'> = async (ctx, payload) => {
  const old = await fetchRow(ctx, 'character_equipment', payload.equipment_id)
  const amount = payload.amount ?? 1
  const newQty = Math.max(0, old.quantity - amount)

  await ctx.supabase
    .from('character_equipment')
    .update({ quantity: newQty })
    .eq('id', payload.equipment_id)

  return {
    effects: [{
      character_id: ctx.characterId,
      target_table: 'character_equipment',
      target_row_id: payload.equipment_id,
      target_field: 'quantity',
      operation: 'UPDATE' as const,
      old_value: old.quantity,
      new_value: newQty,
    }],
    label: `Use ${old.item_name}${amount > 1 ? ` x${amount}` : ''} (${newQty} left)`,
  }
}

// ── PUT IN CONTAINER ─────────────────────────────────────────────────────────

export const handlePutInContainer: ActionHandler<'PUT_IN_CONTAINER'> = async (ctx, payload) => {
  const item = await fetchRow(ctx, 'character_equipment', payload.equipment_id)
  const container = await fetchRow(ctx, 'character_equipment', payload.container_id)
  const effects: EffectRecord[] = []

  const updates: Row = { parent_item_id: payload.container_id }
  // Inherit container's location
  if (item.location !== container.location) {
    updates.location = container.location
    updates.is_carried = container.is_carried
  }

  await ctx.supabase
    .from('character_equipment')
    .update(updates)
    .eq('id', payload.equipment_id)

  effects.push({ character_id: ctx.characterId, target_table: 'character_equipment', target_row_id: payload.equipment_id, target_field: 'parent_item_id', operation: 'UPDATE', old_value: item.parent_item_id, new_value: payload.container_id })

  if (updates.location) {
    effects.push({ character_id: ctx.characterId, target_table: 'character_equipment', target_row_id: payload.equipment_id, target_field: 'location', operation: 'UPDATE', old_value: item.location, new_value: updates.location })
  }

  return { effects, label: `Put ${item.item_name} in ${container.item_name}` }
}

// ── REMOVE FROM CONTAINER ────────────────────────────────────────────────────

export const handleRemoveFromContainer: ActionHandler<'REMOVE_FROM_CONTAINER'> = async (ctx, payload) => {
  const item = await fetchRow(ctx, 'character_equipment', payload.equipment_id)

  await ctx.supabase
    .from('character_equipment')
    .update({ parent_item_id: null })
    .eq('id', payload.equipment_id)

  return {
    effects: [{
      character_id: ctx.characterId,
      target_table: 'character_equipment',
      target_row_id: payload.equipment_id,
      target_field: 'parent_item_id',
      operation: 'UPDATE' as const,
      old_value: item.parent_item_id,
      new_value: null,
    }],
    label: `Remove ${item.item_name} from container`,
  }
}
